import {
  EArrowDirection,
  ESubRollingOfferCollectBehavior,
  ESubRollingOfferCollectMode,
} from 'utils/enums';
import { SequencedProduct, SequencedProductRollingOffer } from 'utils/types';
import { SubRollingOfferProps, SubRollingOfferUiProps } from './types';

export const arrowHeightWidth = 20;
export const containerPadding = 12;
export const subOfferHeight = 152;
export const maxHeight = 546;
const MAX_VISIBLE_SUB_OFFERS = 6;
const OFFER_ARROW_PAIR_RATIO = 2;

const getCollectMode = ({
  indexToCollect,
  offerIndex,
}: {
  indexToCollect: number;
  offerIndex: number;
}): ESubRollingOfferCollectMode => {
  if (offerIndex < indexToCollect) return ESubRollingOfferCollectMode.COLLECTED;
  if (offerIndex > indexToCollect) return ESubRollingOfferCollectMode.LOCKED;

  return ESubRollingOfferCollectMode.AVAILABLE;
};

const filterOffersByCollectMode = ({
  subRollingOffersProducts,
  collectBehavior,
  indexToCollect,
}: {
  subRollingOffersProducts: SequencedProduct[];
  collectBehavior: ESubRollingOfferCollectBehavior;
  indexToCollect: number;
}): SequencedProduct[] => {
  // If collect mode is stay, we don't need to filter out collected offers
  if (collectBehavior === ESubRollingOfferCollectBehavior.STAY)
    return subRollingOffersProducts;

  return subRollingOffersProducts.filter(
    ({ index }) => index >= indexToCollect,
  );
};

const getSubRollingOffersFromProducts = ({
  indexToCollect,
  subRollingOffersProducts,
  subRollingOfferUiProps,
  collectBehavior,
  selectOffer,
}: {
  indexToCollect: number;
  subRollingOffersProducts: SequencedProductRollingOffer[];
  subRollingOfferUiProps: SubRollingOfferUiProps;
  collectBehavior: ESubRollingOfferCollectBehavior;
  selectOffer: () => void;
}): SubRollingOfferProps[] => {
  const filteredOffersProducts: SequencedProductRollingOffer[] =
    filterOffersByCollectMode({
      subRollingOffersProducts,
      collectBehavior,
      indexToCollect,
    });
  return filteredOffersProducts.map(
    (subRollingOfferProduct: SequencedProductRollingOffer) => ({
      ...subRollingOfferProduct,
      ...subRollingOfferUiProps,
      selectOffer,
      index: subRollingOfferProduct.index,
      collectMode: getCollectMode({
        indexToCollect,
        offerIndex: subRollingOfferProduct.index,
      }),
      emblemBadges: subRollingOfferProduct.offerBadges?.emblems ?? [],
    }),
  );
};

export const rollingOfferUtils = {
  getSubRollingOffersFromProducts,
};

const getSubOfferDomIndex = (subOfferCount: number) =>
  subOfferCount * OFFER_ARROW_PAIR_RATIO - 2;

export const checkIfSixthOrLastOfferIsCutOff = (
  container: HTMLDivElement | null,
): boolean => {
  if (!container || container.children.length === 0) return false;

  const totalSubOffers = container.children.length / OFFER_ARROW_PAIR_RATIO;
  const visibleSubOffers = Math.min(totalSubOffers, MAX_VISIBLE_SUB_OFFERS);
  const targetIndex = getSubOfferDomIndex(visibleSubOffers);

  const targetChild = container.children[targetIndex] as
    | HTMLElement
    | undefined;
  if (!targetChild) return true;

  const containerRight = container.scrollLeft + container.offsetWidth;
  const targetRight = targetChild.offsetLeft + targetChild.offsetWidth;

  return targetRight > containerRight;
};

export const scrollToOfferLimit = (
  container: HTMLDivElement,
  totalSubOffers: number,
  onScrollEnd: () => void,
) => {
  const visibleSubOffers = Math.min(totalSubOffers, MAX_VISIBLE_SUB_OFFERS);
  const targetIndex = getSubOfferDomIndex(visibleSubOffers);
  const targetChild = container.children[targetIndex] as
    | HTMLElement
    | undefined;
  if (!targetChild) return;

  const scrollAmount =
    targetChild.offsetLeft +
    targetChild.offsetWidth -
    container.offsetWidth +
    30;

  container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
  onScrollEnd();
};

export const mapArrowDirection: Record<number, EArrowDirection> = {
  [0]: EArrowDirection.RIGHT,
  [1]: EArrowDirection.DOWN,
  [2]: EArrowDirection.LEFT,
  [3]: EArrowDirection.DOWN,
};

export const getRotationDegrees = (dir: EArrowDirection) => {
  switch (dir) {
    case EArrowDirection.DOWN:
      return 'rotate(90deg)';
    case EArrowDirection.LEFT:
      return 'rotate(180deg)';
    case EArrowDirection.RIGHT:
      return 'rotate(0deg)';
    default:
      return 'rotate(0deg)';
  }
};
